import Vue from 'vue'
import Vuex from 'vuex'

import api from '@/api';

import eventBus from '@/eventBus'

Vue.use(Vuex);

const store = new Vuex.Store({
  state: {
    member: null,
    loginError: '',
    signupError: '',
    resetPwError: '',
    forgotPwError: '',
    subscriptionError: '',
    getPathsError: '',

    displayError: [],
    pathsTree: null,
    elements: null,
    pathsList: [],
    recentPaths: [],
    tagList: [],
    reactionTags: null,
    pigs: [],
    records: [],
    verifiedConnections: [],
    lastRoute: ''
  },

  mutations: {
    setMember (state, member) {
      state.member = member;
    },

    clearMember (state, member) {
      state.member = null;
    },

    clearAll (state) {
      state.member = null;
      state.pathsTree = null;
      state.pathsList = [];
      state.recentPaths = [];
      state.tagList = [];
      state.pigs = [];
      state.records = [];
      state.lastRoute = '';
    },

    setError(state, {name, err}) {
      state[name] = err.response.data;
    },

    // NOTE: this is the new method for displaying errors
    // it takes the name and message, not the error object
    setDisplayError(state, {name, msg}) {
      state.displayError = [name, msg];
    },

    clearErrors(state) {
      state.loginError = '';
      state.signupError = '';
      state.resetPwError = '';
      state.forgotPwError = '';
    },

    setElements(state, elements) {
      state.elements = elements
    },

    setPaths (state, {paths, recent}) {
      state.pathsTree = paths.tree;
      state.pathsList = paths.list;
      state.recentPaths = recent;
    },

    setTags (state, res) {
      state.tagList = res.all.map(t => t.tag);
      state.reactionTags = res.reactions.reduce((acc, next) => {
        return Object.assign(acc, {[next.tag]: next.count})
      }, {});
    },

    setPigs (state, pigs) {
      state.pigs = pigs;
    },

    setRecords (state, {records, page}) {
      if (!page) {
        state.records = records;
      } else {
        state.records = state.records.concat(records);
      }
    },

    setVerifiedConnections (state, connections) {
      state.verifiedConnections = connections
    },

    setLastRoute (state, name) {
      state.lastRoute = name
    }
  },

  getters: {
    isLoggedIn (state) {
      return state.member ? true : false;
    },

    isPaidCustomer (state) {
      if (!state.member) return false;
      return ['alpha', 'year_basic', 'month_basic'].indexOf(state.member.plan_name) !== -1;
    },

    isWithoutEmail (state) {
      return !state.member || !state.member.email;
    },

    isUnverifiedMember (state) {
      return !state.member || !state.member.email_verified;
    }
  },

  actions: {
    async login ({commit}, {username, password}) {
      const {res, err} = await api.logIn(username, password);

      if (err) {
        commit('setError', {name: 'loginError', err})
        return false;
      }

      commit('setMember', res);
    },

    async logout ({commit}) {
      await api.logOut();
      commit('clearAll');
      window.location.reload();
    },

    async signup ({commit}, {email, pw}) {
      const {err, res} = await api.signup(email, pw);

      if (err) {
        commit('setError', {name: 'signupError', err})
        return false;
      }

      commit('setMember', res);
    },
    async forgotPw ({commit}, {username}) {
      const {err} = await api.forgot(username);

      if (err) {
        commit('setError', {name: 'forgotPwError', err})
        return false;
      }
    },

    async resetPw ({commit}, {pw, token}) {
      const {err} = await api.resetPw(pw, token)

      if (err) {
        commit('setError', {name: 'resetPwError', err})
        return false;
      }
    },

    async upgradeSub ({commit}, {token, plan, promo}) {
      const {res, err} = await api.upgradeSub(token, plan, promo);
      
      if (err) {
        commit('setError', {name: 'subscriptionError', err})
        return;
      }

      commit('setMember', res);
    },

    async downgradeSub ({commit}, {subscriptionId}) {
      const {res, err} = await api.downgradeSub(subscriptionId);

      commit('setMember', res);

      if (err) {
        commit('setError', {name: 'subscriptionError', err})
        return;
      }
    },

    async getMember ({commit}) {
      const {res, err} = await api.getMember();
      commit('setMember', res);
    },

    async updateMemberPhoneNumber ({ commit }, { phoneNumber }) {
      const {res, err} = await api.updateMemberPhoneNumber(phoneNumber);

      if (err) {
        commit('setError', {name: 'phoneNumberUpdateError', err})
        return;
      }

      commit('setMember', res);
    },

    async updateMemberPrefReactionGroup ({ commit }, { pref }) {
      const {res, err} = await api.updateMemberPrefReactionGroup(pref);

      if (err) {
        commit('setDisplayError', {name: 'prefReactionGroupError', msg: err.response.data})
        return;
      }

      commit('setMember', res);
    },

    async updateMemberHandle ({ commit }, handle) {
      const {res, err} = await api.updateMemberHandle(handle);

      if (err) {
        commit('setDisplayError', {name: 'handleError', msg: err.response.data})
        return;
      }

      commit('setMember', res);
    },

    async updateMemberPrefs ({ commit }, prefs) {
      const {res, err} = await api.updateMemberPrefs(prefs);

      if (err) {
        commit('setDisplayError', {name: 'prefsError', msg: err.response.data})
        return;
      }

      commit('setMember', res);
    },

    async resendMemberEmailVerification ({commit}) {
      const {res, err} = await api.resendMemberEmailVerification();

      if (err) {
        commit('setError', {name: 'emailUpdateError', err})
        return;
      }

      commit('setMember', res);
    },

    async resendMemberNumberVerification ({commit}) {
      const {res, err} = await api.resendMemberNumberVerification();

      if (err) {
        commit('setError', {name: 'numberUpdateError', err})
        return;
      }

      commit('setMember', res);
    },


    async verifyMemberEmail ({ commit }, { token }) {
      const {res, err} = await api.verifyMemberEmail(token);

      if (err) {
        commit('setError', {name: 'emailVerifyError', err})
        return;
      }
      if (res.member) {
        commit('setMember', res.member);
      }

      return res.success;
    },

    async verifyMemberNumber ({ commit }, { token }) {
      const {res, err} = await api.verifyMemberNumber(token);

      if (err) {
        commit('setError', {name: 'numberVerifyError', err})
        return;
      }
      if (res.member) {
        commit('setMember', res.member);
      }

      return res.success;
    },


    async getPaths ({ commit }) {
      const res = await api.getPaths();

      if (res) {
        commit('setPaths', res);
      }
    },

    async getTags ({ commit }) {
      const res = await api.getTags();
      if (res) {
        commit('setTags', res || {all: []});
      }
    },

    async getElements ({ commit }) {
      const res = await api.getElements();
      if (res) {
        commit('setElements', res || null);
      }
    },

    async getPigs ({ commit }) {
      const {res, err} = await api.getPigs();
      if (err) {
        if ( err.response ) {
          return {err: err.response.data};
        } else {
          return err;
        }
        
      }

      commit('setPigs', res);
    },

    async updateRecord ({commit}, {id, text, path, date, isPublic}) {
      const {res, err} = await api.updateMessage(id, text, path, date, isPublic);

      if (err) {
        if ( err.response ) {
          return {err: err.response.data};
        } else {
          return err;
        }
      }
    },

    async deleteRecord ({commit}, {id}) {
      const {res, err} = await api.deleteMessage(id);

      if (err) {
        if ( err.response ) {
          return {err: err.response.data};
        } else {
          return err;
        }
      }
    },

    async getRecords ({commit}, {path, todo, tags, page, start, end, type}) {

      const {res, err} = await api.getRecords(
        type,
        path,
        todo === 'todo' ? true : todo === 'done' ? false : undefined,
        tags,
        start,
        end,
        page);

      if (err) {
        if ( err.response ) {
          return {err: err.response.data};
        } else {
          return err;
        }
      }

      commit('setRecords', {records: res, page});

      return res;
    },

    async searchRecords ({commit}, {query, page}) {
      const {res, err} = await api.searchRecords(
        query,
        page);

      if (err) {
        if ( err.response ) {
          return {err: err.response.data};
        } else {
          return err;
        }
      }

      commit('setRecords', {records: res, page});

      return res;
    },

    async getVerifiedConnections ({commit}) {
      const {res, err} = await api.getVerifiedConnections();

      if (err) {
        if ( err.response ) {
          return {err: err.response.data};
        } else {
          return err;
        }
      }

      commit('setVerifiedConnections', res);

      return res;
    },


    async addRecord ({commit}, {raw, isPublic}) {
      const {res, err} = await api.addRecord(raw, isPublic);

      if (err) {
        if ( err.response ) {
          return {err: err.response.data};
        } else {
          return err;
        }
      }
    },

    async bulkAddRecords ({commit}, {notes, type}) {
      const {res, err} = await api.bulkAddRecords(notes, type);
      if (err) {
        if ( err.response ) {
          return {err: err.response.data};
        } else {
          return err;
        }
      }

      return res
    },

    async init ({dispatch}) {
      await dispatch('getMember');

      await Promise.all([
        dispatch('getElements'),
        dispatch('getPaths'),
        dispatch('getTags'),
        dispatch('getPigs'),
        dispatch('getVerifiedConnections'),
        api.updateMemberTz(
          Intl.DateTimeFormat().resolvedOptions().timeZone)
      ]);
    }
  }
})

eventBus.$on('record', async () => {
  await Promise.all([store.dispatch('getElements')])

  eventBus.$emit('after-record-sync')
})

export default store;
